/**
 * "23 already in other folders · KPOP broad pages (9) · movie edits (6) · +3 folders"
 *
 * Creators this search found that already sit in other folders used to be
 * skipped silently. This strip says how many, where, and who has them — one
 * line until opened — and lets the admin pull any of them into this folder
 * too (they stay in their other folders). Adding them costs nothing: the
 * server copies what the search already fetched (contract §5).
 */
import { useMemo, useState, type JSX } from 'react';
import { Check, ChevronRight, FolderOpen, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PlatformIcon } from '@/components/PlatformIcon';
import { RangeSelectHint, RowCheckbox, SelectAllCheckbox, selectableRowClass } from '@/components/ui/range-select';
import { applyToSet, useRangeSelection, type RangeCheckboxProps, type RangeRowProps } from '@/hooks/useRangeSelection';
import { cn } from '@/lib/utils';
import { OutreachStatusBadge } from '../components/OutreachStatus';
import { profileUrl, toPlatform } from '../lib/platform';
import type { JobOverlap, OverlapCreator } from '../api/types';
import { useIncludeCreators } from './hooks';
import { creatorKey, groupOverlapByFolder, overlapSummary, whoHasThem } from './insights';
import { compactNumber } from './params';

const num = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? compactNumber(n) : '—');

export function OverlapPanel({
  overlap,
  jobId,
  folderPublicId,
  folderName,
}: {
  overlap: JobOverlap | null;
  jobId: string | null;
  folderPublicId: string;
  /** "Include selected in <this folder>". */
  folderName?: string | null;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Included this session, before the next poll reports it.
  const [included, setIncluded] = useState<Set<string>>(new Set());
  const include = useIncludeCreators(jobId, folderPublicId);
  const groups = useMemo(() => (overlap ? groupOverlapByFolder(overlap) : []), [overlap]);
  // Rows in on-screen order; a creator in two folders is listed twice but is one key.
  const keys = useMemo(() => [...new Set(groups.flatMap((g) => g.creators.map(creatorKey)))], [groups]);
  const byKey = useMemo(() => new Map((overlap?.creators ?? []).map((c) => [creatorKey(c), c])), [overlap]);
  const isIncluded = (c: OverlapCreator) => c.included || included.has(creatorKey(c));
  const range = useRangeSelection({
    visible: open ? keys : [],
    isSelected: (k) => selected.has(k),
    onApply: applyToSet(setSelected),
    isDisabled: (k) => {
      const c = byKey.get(k);
      return !c || isIncluded(c);
    },
  });

  if (!overlap || overlap.total <= 0) return null;
  const summary = overlapSummary(overlap);
  const target = folderName ? `“${folderName}”` : 'this folder';

  function add(keys: string[]) {
    const creators = keys.map((k) => byKey.get(k)).filter((c): c is OverlapCreator => !!c && !isIncluded(c));
    if (creators.length === 0) return;
    include.mutate(
      { usernames: creators.map((c) => c.username), source: 'overlap' },
      {
        onSuccess: (res) => {
          setIncluded((prev) => new Set([...prev, ...creators.map(creatorKey)]));
          setSelected((prev) => new Set([...prev].filter((k) => !creators.some((c) => creatorKey(c) === k))));
          const added = typeof res?.added === 'number' ? res.added : creators.length;
          toast.success(`Added ${added} creator${added === 1 ? '' : 's'} to ${folderName ?? 'this folder'}`);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not add them'),
      },
    );
  }

  const selectable = [...selected].filter((k) => {
    const c = byKey.get(k);
    return c && !isIncluded(c);
  });

  return (
    <div className="rounded-lg border border-border bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-accent/40"
      >
        <Users className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-semibold">
            {summary.total === 1 ? '1 already in another folder' : `${summary.total.toLocaleString()} already in other folders`}
          </span>
          {summary.named.map((f) => (
            <span key={f.name} className="text-muted-foreground">
              {' · '}
              {f.name} ({f.count.toLocaleString()})
            </span>
          ))}
          {summary.moreFolders > 0 && (
            <span className="text-muted-foreground">
              {' · '}+{summary.moreFolders} folder{summary.moreFolders === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">{open ? 'Hide' : 'Show who'}</span>
        <ChevronRight
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150', open && 'rotate-90')}
        />
      </button>

      {open && (
        <div className="space-y-3 border-t border-border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <SelectAllCheckbox
              label="Select every creator not yet included"
              state={range.headState}
              disabled={range.selectable.length === 0}
              onToggle={range.toggleAll}
            />
            <p className="min-w-0 flex-1 text-xs text-muted-foreground">
              Skipped because another folder has them. Include any you want here too; they stay in their other folders,
              and adding them doesn't search again.
              <RangeSelectHint show={selectable.length > 0} className="ml-1" />
            </p>
            <Button type="button" size="sm" disabled={selectable.length === 0 || include.isPending || !jobId} onClick={() => add(selectable)}>
              {include.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Include selected in {target}
              {selectable.length > 0 && ` (${selectable.length})`}
            </Button>
          </div>
          <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
            {groups.map((g) => {
              const remaining = g.creators.filter((c) => !isIncluded(c));
              return (
                <section key={g.folderPublicId} aria-label={g.name}>
                  <div className="mb-1 flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                    <h4 className="min-w-0 flex-1 truncate text-sm font-semibold">
                      {g.name} <span className="font-normal text-muted-foreground">({g.count.toLocaleString()})</span>
                    </h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={remaining.length === 0 || include.isPending || !jobId}
                      onClick={() => add(remaining.map(creatorKey))}
                    >
                      {remaining.length === 0 ? 'All included' : `Include all from ${g.name}`}
                    </Button>
                  </div>
                  <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                    {g.creators.map((c) => (
                      <OverlapRow
                        key={`${g.folderPublicId}-${creatorKey(c)}`}
                        creator={c}
                        included={isIncluded(c)}
                        selected={selected.has(creatorKey(c))}
                        rowProps={range.rowProps(creatorKey(c))}
                        checkboxProps={range.checkboxProps(creatorKey(c))}
                      />
                    ))}
                    {g.count > g.creators.length && (
                      <li className="px-3 py-1.5 text-xs text-muted-foreground">
                        and {(g.count - g.creators.length).toLocaleString()} more not listed
                      </li>
                    )}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function OverlapRow({
  creator: c,
  included,
  selected,
  rowProps,
  checkboxProps,
}: {
  creator: OverlapCreator;
  included: boolean;
  selected: boolean;
  rowProps: RangeRowProps;
  checkboxProps: RangeCheckboxProps;
}): JSX.Element {
  const who = whoHasThem(c);
  return (
    <li
      className={cn(
        'flex items-center gap-2.5 px-3 py-1.5',
        selected && !included && 'bg-brand/10',
        !included && cn('transition-colors hover:bg-accent/40', selectableRowClass()),
      )}
      {...(included ? {} : rowProps)}
    >
      {included ? (
        <Check className="h-4 w-4 shrink-0 text-success-strong" aria-label="Included" />
      ) : (
        <RowCheckbox
          aria-label={`Select @${c.username}`}
          className="rounded border-input"
          {...checkboxProps}
        />
      )}
      <PlatformIcon platform={toPlatform(c.platform)} size={14} className="shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <a
          href={profileUrl(c.platform ?? undefined, c.username)}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate text-sm font-medium hover:underline"
        >
          @{c.username}
        </a>
        <span className="block truncate text-xs text-muted-foreground">
          {num(c.followers)} followers · {num(c.avgViews)} avg views
          {who && ` · ${who}`}
          {included && ' · included here'}
        </span>
      </div>
      <span className="hidden shrink-0 sm:inline">
        <OutreachStatusBadge creator={{ outreachState: c.outreachState, outreachClaimedByName: c.claimedByName }} />
      </span>
    </li>
  );
}
